import {
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ContentCutRoundedIcon from "@mui/icons-material/ContentCutRounded";
import MergeTypeRoundedIcon from "@mui/icons-material/MergeTypeRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import { ClipboardChip, SelectionPill, ToolbarDivider, ToolbarSpacer, ToolbarWrapper } from "../style";
import useLanguage from "../../../hooks/useLanguage";

export default function EditorToolbar({
  selectedScene,
  selectionSize,
  onBack,
  onSplit,
  onMerge,
  canMerge,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSave,
  saving,
  clipboardHasCrop,
  onOverlay,
  overlayChip,
  onShortcuts,
}) {
  const [{ pages: { reframeEditor: text } }] = useLanguage();

  const multi = selectionSize && selectionSize > 1;

  const iconBtn = {
    color: "rgba(255,255,255,0.7)",
    width: 28,
    height: 28,
    "&:hover": { color: "#9b46e1", backgroundColor: "rgba(155,70,225,0.08)" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
  };

  return (
    <ToolbarWrapper>
      <Tooltip title={text.toolbar.back} placement="bottom">
        <IconButton size="small" onClick={onBack} sx={{ color: "rgba(255,255,255,0.7)" }}>
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <ToolbarDivider />

      <Tooltip title={`${text.toolbar.undo} (Ctrl+Z)`} placement="bottom">
        <span>
          <IconButton size="small" onClick={onUndo} disabled={!canUndo} sx={iconBtn}>
            <UndoRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`${text.toolbar.redo} (Ctrl+Shift+Z)`} placement="bottom">
        <span>
          <IconButton size="small" onClick={onRedo} disabled={!canRedo} sx={iconBtn}>
            <RedoRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <ToolbarDivider />

      {selectedScene ? (
        <>
          <Tooltip title={`${text.toolbar.split} (S)`} placement="bottom">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCutRoundedIcon sx={{ fontSize: "14px !important" }} />}
              onClick={onSplit}
              sx={{
                color: "rgba(255,255,255,0.7)",
                borderColor: "rgba(255,255,255,0.15)",
                fontSize: "0.72rem",
                height: 28,
                px: 1.5,
                "&:hover": { borderColor: "rgba(155,70,225,0.5)", color: "#9b46e1" },
              }}
            >
              {text.toolbar.split}
            </Button>
          </Tooltip>

          <Tooltip title={multi ? `${text.toolbar.mergeSelected} (M)` : `${text.toolbar.merge} (M)`} placement="bottom">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MergeTypeRoundedIcon sx={{ fontSize: "14px !important" }} />}
                onClick={onMerge}
                disabled={!canMerge}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  borderColor: "rgba(255,255,255,0.15)",
                  fontSize: "0.72rem",
                  height: 28,
                  px: 1.5,
                  "&:hover": { borderColor: "rgba(247,201,72,0.5)", color: "#f7c948" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.08)" },
                }}
              >
                {multi ? text.toolbar.mergeSelected : text.toolbar.merge}
              </Button>
            </span>
          </Tooltip>
        </>
      ) : (
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>
          {text.toolbar.noScene}
        </Typography>
      )}

      <ToolbarSpacer />

      {clipboardHasCrop && (
        <ClipboardChip>
          <ContentPasteRoundedIcon sx={{ fontSize: 12 }} />
          {text.toolbar.clipboardChip}
        </ClipboardChip>
      )}

      {multi && (
        <SelectionPill>
          {text.toolbar.multiSelected.replace("{n}", selectionSize)}
        </SelectionPill>
      )}

      <ToolbarDivider />

      <Tooltip title={`${text.toolbar.shortcuts} (?)`} placement="bottom">
        <IconButton size="small" onClick={onShortcuts} sx={iconBtn}>
          <KeyboardRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={text.toolbar.overlay} placement="bottom">
        <IconButton
          size="small"
          onClick={onOverlay}
          sx={{
            ...iconBtn,
            ...(overlayChip ? { color: "#c89bf2" } : {}),
          }}
        >
          <LayersRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {overlayChip && (
        <Chip
          label={overlayChip}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.62rem",
            backgroundColor: "rgba(155,70,225,0.18)",
            color: "#c89bf2",
            border: "1px solid rgba(155,70,225,0.45)",
            "& .MuiChip-label": { px: 1 },
          }}
        />
      )}

      <Button
        size="small"
        variant="contained"
        startIcon={<SaveRoundedIcon sx={{ fontSize: "14px !important" }} />}
        onClick={onSave}
        disabled={saving}
        sx={{
          backgroundColor: "#9b46e1",
          color: "#fff",
          fontSize: "0.72rem",
          height: 28,
          px: 2,
          ml: 1,
          "&:hover": { backgroundColor: "#7d35b8" },
          "&.Mui-disabled": { backgroundColor: "rgba(155,70,225,0.3)", color: "rgba(255,255,255,0.4)" },
        }}
      >
        {saving ? text.toolbar.saving : text.toolbar.save}
      </Button>
    </ToolbarWrapper>
  );
}
